import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { aiPairingService } from "../services/aiPairingService";
import "./PairingFeedback.css";

/**
 * PairingFeedback Component - Phase 2.2 Implementation
 *
 * Guest feedback system for pairing recommendations,
 * allowing users to rate and provide feedback on AI suggestions.
 */
const PairingFeedback = ({
  restaurantId,
  pairingId = null,
  beverageName = "",
  foodName = "",
  onFeedbackSubmit = null,
  showInline = false,
}) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const feedbackReasons = {
    positive: [
      "Perfect flavor combination",
      "Enhanced the dining experience",
      "Surprised me in a good way",
      "Professional recommendation",
      "Great value pairing",
    ],
    negative: [
      "Flavors clashed",
      "Too expensive",
      "Not what I expected",
      "Poor timing/temperature",
      "Would prefer something else",
    ],
  };

  const quickFeedbackOptions = [
    { emoji: "🤩", label: "Amazing!", value: 5 },
    { emoji: "😊", label: "Great", value: 4 },
    { emoji: "😐", label: "Okay", value: 3 },
    { emoji: "😕", label: "Poor", value: 2 },
    { emoji: "😞", label: "Terrible", value: 1 },
  ];

  useEffect(() => {
    // Reset form when pairing changes
    if (pairingId) {
      resetForm();
    }
  }, [pairingId]);

  const resetForm = () => {
    setRating(0);
    setHoverRating(0);
    setFeedback("");
    setSelectedReasons([]);
    setIsSubmitted(false);
    setError(null);
  };

  const handleStarClick = (starRating) => {
    setRating(starRating);
  };

  const handleStarHover = (starRating) => {
    setHoverRating(starRating);
  };

  const handleStarLeave = () => {
    setHoverRating(0);
  };

  const handleQuickFeedback = (value) => {
    setRating(value);
  };

  const toggleReason = (reason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      setError("Please provide a rating");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const feedbackData = {
        restaurantId,
        pairingId,
        beverageName,
        foodName,
        rating,
        feedback: feedback.trim(),
        reasons: selectedReasons,
        timestamp: new Date().toISOString(),
        source: "pairing-interface",
      };

      // Submit feedback through AI pairing service
      await aiPairingService.submitPairingFeedback(feedbackData);

      setIsSubmitted(true);

      if (onFeedbackSubmit) {
        onFeedbackSubmit(feedbackData);
      }

      // Auto-hide after success (if inline)
      if (showInline) {
        setTimeout(() => {
          setIsSubmitted(false);
        }, 3000);
      }
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setError("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStarRating = () => (
    <div className="star-rating">
      <h4>How was this pairing?</h4>
      <div className="stars-container">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star ${
              star <= (hoverRating || rating) ? "active" : ""
            }`}
            onClick={() => handleStarClick(star)}
            onMouseEnter={() => handleStarHover(star)}
            onMouseLeave={handleStarLeave}
          >
            ★
          </button>
        ))}
      </div>
      <p className="rating-label">{getRatingLabel(hoverRating || rating)}</p>
    </div>
  );

  const renderQuickFeedback = () => (
    <div className="quick-feedback">
      <h4>Quick Rating</h4>
      <div className="quick-options">
        {quickFeedbackOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`quick-option ${
              rating === option.value ? "selected" : ""
            }`}
            onClick={() => handleQuickFeedback(option.value)}
          >
            <span className="option-emoji">{option.emoji}</span>
            <span className="option-label">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderReasonSelection = () => {
    const reasons =
      rating >= 4 ? feedbackReasons.positive : feedbackReasons.negative;
    const sectionTitle =
      rating >= 4 ? "What made it great?" : "What could be improved?";

    return (
      <div className="reason-selection">
        <h4>{sectionTitle}</h4>
        <div className="reason-options">
          {reasons.map((reason) => (
            <label key={reason} className="reason-option">
              <input
                type="checkbox"
                checked={selectedReasons.includes(reason)}
                onChange={() => toggleReason(reason)}
              />
              <span className="reason-label">{reason}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const getRatingLabel = (ratingValue) => {
    if (ratingValue === 0) return "Select a rating";
    if (ratingValue === 1) return "Terrible";
    if (ratingValue === 2) return "Poor";
    if (ratingValue === 3) return "Okay";
    if (ratingValue === 4) return "Great";
    if (ratingValue === 5) return "Amazing!";
    return "";
  };

  if (isSubmitted) {
    return (
      <div className={`pairing-feedback ${showInline ? "inline" : ""}`}>
        <div className="feedback-success">
          <div className="success-icon">✅</div>
          <h3>Thank You!</h3>
          <p>Your feedback helps us improve our pairing recommendations.</p>
          {!showInline && (
            <button onClick={resetForm} className="new-feedback-button">
              Provide More Feedback
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`pairing-feedback ${showInline ? "inline" : ""}`}>
      <div className="feedback-header">
        <h3>💬 Rate This Pairing</h3>
        {beverageName && foodName && (
          <p className="pairing-description">
            <strong>{beverageName}</strong> paired with{" "}
            <strong>{foodName}</strong>
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="feedback-form">
        {showInline ? renderQuickFeedback() : renderStarRating()}

        {rating > 0 && rating !== 3 && (
          <div className="conditional-section">{renderReasonSelection()}</div>
        )}

        <div className="text-feedback">
          <h4>Additional Comments (Optional)</h4>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell us more about your experience with this pairing..."
            className="feedback-textarea"
            rows={showInline ? 2 : 4}
            maxLength={500}
          />
          <div className="character-count">
            {feedback.length}/500 characters
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-actions">
          {!showInline && (
            <button type="button" onClick={resetForm} className="reset-button">
              Reset
            </button>
          )}
          <button
            type="submit"
            disabled={rating === 0 || isSubmitting}
            className="submit-button"
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </button>
        </div>
      </form>
    </div>
  );
};

PairingFeedback.propTypes = {
  restaurantId: PropTypes.string.isRequired,
  pairingId: PropTypes.string,
  beverageName: PropTypes.string,
  foodName: PropTypes.string,
  onFeedbackSubmit: PropTypes.func,
  showInline: PropTypes.bool,
};

PairingFeedback.defaultProps = {
  pairingId: null,
  beverageName: "",
  foodName: "",
  onFeedbackSubmit: null,
  showInline: false,
};

export default PairingFeedback;
